// "The portal asked for a sign-in" (brief 15, item 5).
//
// The HTTP runner stops with NEEDS_SIGN_IN when the portal asks for a second factor
// or device check, or the credentials do not reach the home page, and writes that
// into the run's notes. The Submit queue page reads the state beside its list
// (`fetchSignInState`) and hands it here; this banner shows it with two actions:
// open the portal's sign-in page in a new tab (the operator signs in there, on the
// dedicated profile — Atlas never holds a credential), and Retry, which returns the
// stopped run's leases to the queue so the next run takes them. Atlas cannot start
// that run: it runs on the operator's machine, and the banner says so.

import React, { useState } from "react";
import { retrySignIn } from "./api";
import { relativeTime } from "./ageing";
import "./Queue.css";

export default function SignInBanner({
  practiceId,
  signIn,
  error = null,
  readOnly = false,
  onRetried,
  // Portal sessions (docs/portal-sessions.md): when the practice can sign in inside
  // Atlas, QueuePage passes a function that opens the PMS coverage page, where the
  // session panel lives. Absent for a biller (admin and above only) or when the
  // page cannot navigate.
  onOpenSession = null,
}) {
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState(null);
  const [done, setDone] = useState(null);

  const retry = async () => {
    if (retrying) return;
    setRetrying(true);
    setRetryError(null);
    try {
      const result = await retrySignIn(practiceId);
      setDone(result);
      if (onRetried) onRetried(result);
    } catch (err) {
      setRetryError(err.message);
    } finally {
      setRetrying(false);
    }
  };

  if (done) {
    return (
      <div className="sq-message success" role="status">
        <span>
          {done.released} claim{done.released === 1 ? "" : "s"} returned to the
          queue; {done.pending} waiting. Run the skill again from the machine
          that is signed in to the portal.
        </span>
        <button
          className="sq-message-close"
          aria-label="Dismiss"
          onClick={() => setDone(null)}
        >
          ×
        </button>
      </div>
    );
  }

  if (error) {
    // Its own line, never the queue's: the list below is still current.
    return (
      <div className="sq-message error" role="alert">
        <span>{error}</span>
      </div>
    );
  }

  if (!signIn || !signIn.needsSignIn) return null;

  const where = signIn.portalLabel || signIn.portal || "The portal";
  return (
    <div
      className="sq-run-banner red sq-signin"
      role="alert"
      data-testid="needs-sign-in"
    >
      <span>
        <strong>{where} needs a sign-in before the queue can be worked.</strong>{" "}
        The last run{signIn.operator ? ` (${signIn.operator})` : ""}
        {signIn.at ? `, ${relativeTime(signIn.at)},` : ""} stopped:{" "}
        {signIn.message || "NEEDS_SIGN_IN"}. {signIn.pending} claim
        {signIn.pending === 1 ? "" : "s"} waiting.
      </span>
      <span className="sq-signin-actions">
        {onOpenSession && !readOnly && (
          <button
            className="btn btn-primary btn-sm"
            onClick={onOpenSession}
            title="Open a browser on Atlas's side and sign in to the portal from here"
          >
            Sign in inside Atlas
          </button>
        )}
        {signIn.signInUrl ? (
          <a
            className="btn btn-primary btn-sm"
            href={signIn.signInUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Sign in to the portal
          </a>
        ) : (
          <span className="sq-muted">
            The runbook does not say where the sign-in page is.
          </span>
        )}
        {!readOnly && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={retry}
            disabled={retrying}
            title="Return the stopped run's claims to the queue; the next run from the signed-in machine takes them"
          >
            {retrying ? "Retrying…" : "Retry"}
          </button>
        )}
      </span>
      {retryError && <span className="sq-run-detail">{retryError}</span>}
    </div>
  );
}
